import { formatDistance } from 'date-fns';
import { parseInstantDate } from './calendar-date';

/**
 * The "last edited by" stamp on a ticket.
 *
 * The ticket model is last-write-wins by design: a field hand and a dispatcher
 * can both edit the same ticket and the later save silently overwrites the
 * earlier one. There is no locking and no merge — the only mitigation is making
 * the overwrite visible so a human notices. This module decides what that line
 * says; it deliberately does not decide whether a conflict happened.
 *
 * The API returns both fields snake_case and untransformed:
 *   last_edited_by       uuid | null
 *   last_edited_by_name  string | null
 * camelCase will not be found. Read the snake_case keys.
 *
 * NULL is a real and expected value, not an error:
 *   - System-initiated writes (crons, agents, webhooks) record NULL rather than
 *     letting a previous editor ride along with a fresh timestamp.
 *   - Rows edited before migration 0279 have no stamp.
 *   - The API's CASE WHEN guard returns null rather than an empty string.
 *
 * So a missing half is never filled in from the other half, and a missing name
 * never renders as '', 'Unknown', or a stale previous editor.
 */

export type LastEditKind = 'full' | 'name-only' | 'time-only' | 'none';

export interface LastEditDisplay {
  kind: LastEditKind;
  /** The visible line. Always non-empty — 'none' says so out loud. */
  text: string;
  /**
   * Absolute local-time rendering of the instant, or null when there is no
   * usable instant. Never derived from the name.
   */
  absolute: string | null;
}

/**
 * A name counts as present only when it is a non-blank string. The API returns
 * null rather than '', but a blank that slipped through must render as absent
 * rather than as an empty "Edited by ".
 */
function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * updated_at is a timestamptz — a real instant, not a calendar day. It is
 * handed to Date whole so the offset is honoured and it renders in the viewer's
 * zone. Do NOT split on 'T' and rebuild from parts the way work_date/end_date
 * are handled elsewhere in this repo: that treatment is correct for calendar
 * days and wrong for instants, and would pin every stamp to local midnight.
 *
 * The normalisation this needs — Postgres' space separator and bare hour
 * offset, which older JavaScriptCore rejects and V8 accepts — used to live here
 * as a private `toIso`. A second copy of it grew in shift-clock.ts, each with
 * one guard the other lacked, so both now come from `parseInstantDate` in
 * calendar-date.ts. The hazard is documented on that helper.
 */

/**
 * Absolute time in the viewer's own zone, with the zone abbreviation shown so
 * "3:14 PM MDT" can be told apart from the same wall time somewhere else.
 */
export function formatEditedAbsolute(dt: Date): string {
  return dt.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Build the stamp. `now` is injectable so relative time is testable; callers in
 * the app leave it out.
 */
export function describeLastEdit(
  name: unknown,
  updatedAt: unknown,
  now: Date = new Date()
): LastEditDisplay {
  const who = cleanName(name);
  const when = parseInstantDate(updatedAt);
  const absolute = when ? formatEditedAbsolute(when) : null;
  const relative = when ? formatDistance(when, now, { addSuffix: true }) : null;

  if (who && relative) {
    return { kind: 'full', text: `Edited by ${who} · ${relative}`, absolute };
  }
  // A name with no usable timestamp. Say who, and say nothing about when.
  if (who) {
    return { kind: 'name-only', text: `Edited by ${who}`, absolute: null };
  }
  // An instant with no editor: a system write, or a row older than 0279. We
  // know when, we do not know who, and we do not guess.
  if (relative) {
    return {
      kind: 'time-only',
      text: `Edited ${relative} · editor not recorded`,
      absolute,
    };
  }
  return { kind: 'none', text: 'Last edit not recorded', absolute: null };
}

/** Read the stamp off a raw API ticket. snake_case only — see the note above. */
export function lastEditOf(ticket: any, now?: Date): LastEditDisplay {
  return describeLastEdit(ticket?.last_edited_by_name, ticket?.updated_at, now);
}
